/* eslint-disable max-len */
import React, { useEffect, useState } from 'react';

import * as XLSX from 'xlsx';

import axios from 'axios';

import { FILE_TYPE } from '../constants';

import useStyle from './style';

import Loading from 'components/loading';

interface Props {
  path: string;
  name: string;
  fileType: string;
}

const ExcelViewer: React.FC<Props> = ({ path, name, fileType }) => {
  const classes = useStyle();
  const [loading, setLoading] = useState(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<Array<Record<string, any>>>([]);
  const [sheetNames, setSheetNames] = useState<string[]>([]);
  const [selectedSheet, setSelectedSheet] = useState<string | null>(null);
  const [workbook, setWorkbook] = useState<any>(null);

  const parseSheet = (wb: any, sheetName: string | null): void => {
    if (!wb || !sheetName) {
      setHeaders([]);
      setRows([]);
      return;
    }

    const worksheet = wb.Sheets ? wb.Sheets[sheetName] : undefined;
    if (!worksheet) {
      console.error('Worksheet not found for sheet', sheetName);
      setHeaders([]);
      setRows([]);
      return;
    }

    const sheetJsonObjects: any[] = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

    if (
      Array.isArray(sheetJsonObjects) &&
      sheetJsonObjects.length > 0 &&
      sheetJsonObjects[0] !== undefined &&
      typeof sheetJsonObjects[0] === 'object' &&
      !Array.isArray(sheetJsonObjects[0])
    ) {
      const headerRow = Object.keys(sheetJsonObjects[0]);
      setHeaders(headerRow);
      setRows(sheetJsonObjects as Array<Record<string, any>>);
    } else {
      const raw: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
      if (!raw || raw.length === 0) {
        setHeaders([]);
        setRows([]);
        return;
      }

      const [firstRow, ...rest] = raw;
      if (!firstRow || !Array.isArray(firstRow)) {
        console.error('Header row missing or not an array', { raw });
        setHeaders([]);
        setRows([]);
        return;
      }

      const headerRow = firstRow.map((h: any, i: number) =>
        h === undefined || h === null || h === '' ? `Column ${i + 1}` : String(h),
      );

      const formatted = rest.map((r: any[]) => {
        const obj: Record<string, any> = {};
        headerRow.forEach((h, i) => {
          obj[h] = r[i];
        });
        return obj;
      });

      setHeaders(headerRow);
      setRows(formatted);
    }
  };

  useEffect(() => {
    if (!path) return;
    let cancelled = false;

    async function fetchAndParse(): Promise<void> {
      try {
        setLoading(true);
        let wb = null;
        if (fileType === FILE_TYPE.CSV) {
          const response = await axios.get(path, { responseType: 'text', external: true });
          const csvText = response as any as string;
          wb = XLSX.read(csvText, { type: 'string' });
        } else {
          const response = await axios.get(path, {
            responseType: 'arraybuffer',
            external: true,
          });

          const res = response as any as ArrayBuffer;
          if (!res || !(res instanceof ArrayBuffer || (res as any).byteLength)) {
            console.error('Expected ArrayBuffer response from backend');
            setHeaders([]);
            setRows([]);
            return;
          }

          const data = new Uint8Array(res as any as ArrayBuffer);
          wb = XLSX.read(data, { type: 'array' });
        }

        const sheets = wb && Array.isArray(wb.SheetNames) ? wb.SheetNames : [];
        if (sheets.length === 0) {
          console.error('Workbook has no sheets', { wb });
          setHeaders([]);
          setRows([]);
          return;
        }

        setWorkbook(wb);
        setSheetNames(sheets);
        const sheetToParse =
          selectedSheet && sheets.includes(selectedSheet) ? selectedSheet : sheets[0];
        setSelectedSheet(sheetToParse);
        parseSheet(wb, sheetToParse);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error loading File', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchAndParse();

    return () => {
      cancelled = true;
    };
  }, [path]);

  if (loading) return <Loading loading={true} testId={'excel-page-loader'} />;

  if (!rows || rows.length === 0) {
    return <div style={{ padding: 16 }}>No spreadsheet data to display</div>;
  }

  return (
    <div className={classes.excelContainer} data-testid="excel-viewer">
      <div className={classes.excelTitle}>{name}</div>

      <table className={classes.tableContainer}>
        <thead>
          <tr>
            {headers.map((h) => (
              <th key={h} className={classes.tableHeader}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri}>
              {headers.map((h) => (
                <td key={h} className={classes.tableRow}>
                  {row[h] !== undefined && row[h] !== null ? String(row[h]) : ''}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {sheetNames && sheetNames.length > 1 && (
        <div className={classes.sheetTabContainer}>
          {sheetNames.map((s) => (
            <button
              key={s}
              onClick={(): void => {
                if (s === selectedSheet) return;
                setSelectedSheet(s);
                if (workbook) parseSheet(workbook, s);
              }}
              style={{
                background: s === selectedSheet ? '#eee' : '#fff',
              }}
              className={classes.sheetTabButton}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ExcelViewer;
